import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, User, Bell, SlidersHorizontal } from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { useApp } from '../store';
const tabs = [{
  id: 'profile',
  label: 'Profile',
  icon: User
}, {
  id: 'notifications',
  label: 'Notifications',
  icon: Bell
}, {
  id: 'preferences',
  label: 'Preferences',
  icon: SlidersHorizontal
}] as const;
type TabId = (typeof tabs)[number]['id'];
function Toggle({
  checked,
  onChange,
  label,
  description
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
}) {
  return <div className="flex items-center justify-between py-3.5 border-b border-navy-50 last:border-0" data-id="element-679">
      <div className="pr-4" data-id="element-680">
        <p className="text-sm font-semibold text-navy-900" data-id="element-681">{label}</p>
        <p className="text-xs text-navy-400" data-id="element-682">{description}</p>
      </div>
      <button onClick={onChange} role="switch" aria-checked={checked} className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? 'bg-gold-500' : 'bg-navy-200'}`} data-id="element-683">
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} data-id="element-684" />
      </button>
    </div>;
}
export function SettingsPage() {
  const {
    user,
    updateUser
  } = useApp();
  const [tab, setTab] = useState<TabId>('profile');
  // Local editable profile copy
  const [form, setForm] = useState({
    name: user.name,
    title: user.title,
    location: user.location,
    email: user.email
  });
  const [saved, setSaved] = useState(false);
  // Notification + preference toggles (persisted to localStorage)
  const [notifs, setNotifs] = useState(() => {
    try {
      const saved = localStorage.getItem('ihl_notifs');
      if (saved) return JSON.parse(saved);
    } catch {
      // ignore
    }
    return {
      mentions: true,
      recognition: true,
      news: false,
      events: true,
      digest: true
    };
  });
  const [prefs, setPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem('ihl_prefs');
      if (saved) return JSON.parse(saved);
    } catch {
      // ignore
    }
    return {
      compact: false,
      autoplay: true,
      reduceMotion: false
    };
  });
  useEffect(() => {
    try {
      localStorage.setItem('ihl_notifs', JSON.stringify(notifs));
    } catch {
      // ignore
    }
  }, [notifs]);
  useEffect(() => {
    try {
      localStorage.setItem('ihl_prefs', JSON.stringify(prefs));
    } catch {
      // ignore
    }
  }, [prefs]);
  const saveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    updateUser(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return <>
      <PageHeader title="Settings" subtitle="Manage your profile, notifications, and workspace preferences." data-id="element-685" />

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 max-w-4xl" data-id="element-686">
        {/* Tabs */}
        <nav className="flex md:flex-col gap-1 overflow-x-auto" data-id="element-687">
          {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${active ? 'bg-navy-600 text-white' : 'text-navy-600 hover:bg-white'}`} data-id="element-688">
                <Icon className="w-4 h-4" data-id="element-689" /> {t.label}
              </button>;
        })}
        </nav>

        {/* Panel */}
        <motion.div key={tab} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-xl border border-navy-100 p-6" data-id="element-690">
          {tab === 'profile' && <form onSubmit={saveProfile} data-id="element-691">
              <div className="flex items-center gap-4 mb-6" data-id="element-692">
                <img src={user.avatar} alt="" className="w-16 h-16 rounded-full ring-2 ring-gold-400" data-id="element-693" />
                <button type="button" className="px-3 py-1.5 text-sm font-semibold text-navy-700 bg-navy-50 rounded-lg hover:bg-navy-100" data-id="element-694">
                  Change photo
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4" data-id="element-695">
                <Field label="Full name" value={form.name} onChange={v => setForm(f => ({
              ...f,
              name: v
            }))} data-id="element-696" />
                <Field label="Title" value={form.title} onChange={v => setForm(f => ({
              ...f,
              title: v
            }))} data-id="element-697" />
                <Field label="Location" value={form.location} onChange={v => setForm(f => ({
              ...f,
              location: v
            }))} data-id="element-698" />
                <Field label="Work email" type="email" value={form.email} onChange={v => setForm(f => ({
              ...f,
              email: v
            }))} data-id="element-699" />
              </div>
              <div className="flex items-center gap-3 mt-6" data-id="element-700">
                <button type="submit" className="px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-701">
                  Save changes
                </button>
                {saved && <span className="inline-flex items-center gap-1 text-sm font-semibold text-sage" data-id="element-702">
                    <Check className="w-4 h-4" data-id="element-703" /> Saved
                  </span>}
              </div>
            </form>}

          {tab === 'notifications' && <div data-id="element-704">
              <h3 className="font-serif font-bold text-lg text-navy-900 mb-1" data-id="element-705">
                Email & push
              </h3>
              <p className="text-sm text-navy-400 mb-3" data-id="element-706">
                Choose what you'd like to hear about.
              </p>
              <Toggle checked={notifs.mentions} onChange={() => setNotifs((n: any) => ({
            ...n,
            mentions: !n.mentions
          }))} label="Mentions & replies" description="When someone mentions or replies to you." data-id="element-707" />
              <Toggle checked={notifs.recognition} onChange={() => setNotifs((n: any) => ({
            ...n,
            recognition: !n.recognition
          }))} label="Recognition" description="When you receive kudos." data-id="element-708" />
              <Toggle checked={notifs.news} onChange={() => setNotifs((n: any) => ({
            ...n,
            news: !n.news
          }))} label="Company news" description="New articles and announcements." data-id="element-709" />
              <Toggle checked={notifs.events} onChange={() => setNotifs((n: any) => ({
            ...n,
            events: !n.events
          }))} label="Event reminders" description="Before events you've RSVP'd to." data-id="element-710" />
              <Toggle checked={notifs.digest} onChange={() => setNotifs((n: any) => ({
            ...n,
            digest: !n.digest
          }))} label="Weekly digest" description="A Monday summary of what you missed." data-id="element-711" />
            </div>}

          {tab === 'preferences' && <div data-id="element-712">
              <h3 className="font-serif font-bold text-lg text-navy-900 mb-1" data-id="element-713">
                Workspace
              </h3>
              <p className="text-sm text-navy-400 mb-3" data-id="element-714">
                Tune how the intranet looks and behaves.
              </p>
              <Toggle checked={prefs.compact} onChange={() => setPrefs((p: any) => ({
            ...p,
            compact: !p.compact
          }))} label="Compact density" description="Tighter spacing across lists and cards." data-id="element-715" />
              <Toggle checked={prefs.autoplay} onChange={() => setPrefs((p: any) => ({
            ...p,
            autoplay: !p.autoplay
          }))} label="Auto-rotate featured news" description="Cycle the home carousel automatically." data-id="element-716" />
              <Toggle checked={prefs.reduceMotion} onChange={() => setPrefs((p: any) => ({
            ...p,
            reduceMotion: !p.reduceMotion
          }))} label="Reduce motion" description="Minimize animations and transitions." data-id="element-717" />
            </div>}
        </motion.div>
      </div>
    </>;
}
function Field({
  label,
  value,
  onChange,
  type = 'text'
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return <label className="block" data-id="element-718">
      <span className="block text-xs font-semibold uppercase tracking-wide text-navy-500 mb-1.5" data-id="element-719">
        {label}
      </span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2.5 rounded-lg bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900" data-id="element-720" />
    </label>;
}